// Morgan's Quarterly MA Studio (Pepper Potts' Report Studio 2.0): independent copy of the standard variant of MaReportPanel.
import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice } from '../MissionShell/MissionShell';
import { generateMaReportV2, MaReportV2Summary } from '../../api/maReportV2Api';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

const textareaClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground';

function MaReportV2Panel({ mission, onBack }: PanelProps) {
  const [pnl, setPnl] = useState<File[]>([]);
  const [bs, setBs] = useState<File[]>([]);
  const [coa, setCoa] = useState<File[]>([]);
  const [logo, setLogo] = useState<File[]>([]);
  const [clientName, setClientName] = useState('');
  const [subClientName, setSubClientName] = useState('');
  const [notes, setNotes] = useState('');
  const [title, setTitle] = useState('Management Report');
  const [makePptx, setMakePptx] = useState(true);
  const [lastYearDate, setLastYearDate] = useState('');
  const { status, error, result, run } = useFileRun<FileResult<MaReportV2Summary>>();

  const steps = [
    'Upload the Profit & Loss and Balance Sheet Excel exports and the Chart of Accounts CSV.',
    'Last Year figures come from the "Previous Year" column of the P&L file. If last year is a separate export, copy it into the P&L file as a second sheet (e.g. named "Last Year").',
    'Click Generate Report. A PDF (and a PowerPoint, in a ZIP) downloads automatically.',
  ];

  const ready = Boolean(pnl[0] && coa[0] && bs[0]);

  const handleSubmit = async () => {
    if (!ready) return;
    await run(() => generateMaReportV2({
      pnl: pnl[0],
      bs: bs[0] ?? null,
      coa: coa[0],
      logo: logo[0] ?? null,
      reportTitle: title,
      clientName,
      subClientName,
      customNotes: notes,
      makePptx,
      lastYearDate,
    }), 'Report generation failed.');
  };

  return (
    <MissionShell mission={mission} onBack={onBack} steps={steps} wide>
      <div className="grid gap-x-4 sm:grid-cols-2">
        <FileDrop label="Profit & Loss (.xlsx)" required accept=".xlsx" hint="XLSX" accentColor={mission.color} files={pnl} onFiles={setPnl} />
        <FileDrop label="Chart of Accounts (.csv)" required accept=".csv" hint="CSV" accentColor={mission.color} files={coa} onFiles={setCoa} />
        <FileDrop label="Balance Sheet (.xlsx)" required accept=".xlsx" hint="XLSX" accentColor={mission.color} files={bs} onFiles={setBs} />
      </div>

      <div className="mb-5 rounded-lg border border-border bg-muted p-4">
        <p className="mb-3 text-sm font-bold text-foreground">🏷️ Client details</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="ma2-client" className="mb-1.5">
              Client name
            </Label>
            <Input id="ma2-client" value={clientName} onChange={(e) => setClientName(e.target.value)} />
            <p className="mt-1 text-xs text-muted-foreground">Leave blank to use the company name from the P&L file.</p>
          </div>
          <div>
            <Label htmlFor="ma2-sub" className="mb-1.5">
              Sub-client name
            </Label>
            <Input id="ma2-sub" value={subClientName} onChange={(e) => setSubClientName(e.target.value)} />
          </div>
        </div>
        <div className="mt-4">
          <FileDrop label="Logo (optional)" accept=".png,.jpg,.jpeg" hint="PNG or JPG" accentColor={mission.color} files={logo} onFiles={setLogo} />
        </div>
        <div className="mt-2">
          <Label htmlFor="ma2-notes" className="mb-1.5">
            Accountants Reports
          </Label>
          <textarea id="ma2-notes" rows={6} className={textareaClass} value={notes} onChange={(e) => setNotes(e.target.value)} />
          <p className="mt-1 text-xs text-muted-foreground">
            Type or paste the text. It appears as the "Accountants Reports" page right after Summary &amp; Reports, with your line breaks kept.
          </p>
        </div>
        <div className="mt-4 sm:w-1/2 sm:pr-2">
          <Label htmlFor="ma2-last-year-date" className="mb-1.5">
            Last Year date (optional)
          </Label>
          <Input id="ma2-last-year-date" type="date" value={lastYearDate} onChange={(e) => setLastYearDate(e.target.value)} />
          <p className="mt-1 text-xs text-muted-foreground">
            Shown under the "Last Year" heading in the P&amp;L and Overheads tables. Leave blank to show the year.
          </p>
        </div>
        <div className="mt-4 grid items-end gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="ma2-title" className="mb-1.5">
              Report title
            </Label>
            <Input id="ma2-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 pb-2 text-sm text-foreground">
            <input type="checkbox" checked={makePptx} onChange={(e) => setMakePptx(e.target.checked)} />
            Also generate PowerPoint (downloaded as a ZIP with the PDF)
          </label>
        </div>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={!ready || status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Building report…' : '⚡ Generate Report'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Building report…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && (
        <div className="mt-4 space-y-2 text-sm">
          <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">✅ Report generated. {result.filename} downloaded.</div>
          {result.summary?.prior_source ? (
            <div className="rounded-lg bg-blue-50 px-4 py-3 text-blue-700">
              Prior-year comparison taken from the {result.summary.prior_source}.
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              No prior-year data found, so the Last Year columns / Var % are blank. The P&amp;L file needs a
              "Previous Year" column, or last year's P&amp;L export as a second sheet.
            </p>
          )}
        </div>
      )}
      {status === 'idle' && !ready && (
        <InfoNotice>
          Upload the Profit &amp; Loss, Balance Sheet, and Chart of Accounts files to proceed.
        </InfoNotice>
      )}
    </MissionShell>
  );
}

export default MaReportV2Panel;
